import { DataTable, usePagination } from '@/components/table/DataTable';
import { Badge } from '@/components/ui/badge';
import { CountryFilter } from '@/components/ui/country-select';
import {
  DateRangeSelect,
  type DateRange,
} from '@/components/ui/date-range-select';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { useConsultantUpdatesService } from '@/services/consultant';
import { MultiGeographyBadge } from '@/shared/geography/MultiGeographyBadge';
import { compareGeographyCodesForFilter } from '@/shared/geography';
import type { ConsultantUpdateItem } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  Check,
  Compass,
  ExternalLink,
  Gem,
  Rss,
  Search,
  Shield,
} from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useConsultantContext } from './ConsultantLayout';
import { EditSummaryModal } from './components/EditSummaryModal';

const TYPE_OPTIONS: FilterOption[] = [
  { value: 'news', label: 'News' },
  { value: 'guidance', label: 'Guidance' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'standard', label: 'Standard' },
];

const TYPE_BADGE: Record<
  string,
  { label: string; icon: FC<{ className?: string }>; bg: string }
> = {
  regulation: { label: 'Regulation', icon: Shield, bg: 'bg-magenta-100' },
  guidance: { label: 'Guidance', icon: Compass, bg: 'bg-purple-100' },
  standard: { label: 'Standard', icon: Gem, bg: 'bg-orange-100' },
  news: { label: 'News', icon: Rss, bg: 'bg-blue-100' },
};

const ANALYZED_OPTIONS: FilterOption[] = [
  { value: 'yes', label: 'Analyzed' },
  { value: 'no', label: 'Not analyzed' },
];

// ── Option Filter ──

const OptionFilter: FC<{
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const isActive = selected.length > 0;
  const activeLabel =
    selected.length === 1
      ? options.find(o => o.value === selected[0])?.label
      : `${selected.length} selected`;

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={isActive ? activeLabel : undefined}
      onClear={isActive ? () => onChange([]) : undefined}
    >
      <FilterOptionList
        options={options}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};

// ── Helpers ──

const isAnalyzed = (u: ConsultantUpdateItem) =>
  !!(u.consultantShortSummary && u.consultantSummary);

// ── Main Page ──

export const ConsultantUpdatesPage: FC = () => {
  const { orgId, orgName } = useConsultantContext();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [selectedGeographies, setSelectedGeographies] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedAnalyzed, setSelectedAnalyzed] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editItem, setEditItem] = useState<ConsultantUpdateItem | null>(null);

  const baseQuery = useMemo(
    () => ({
      ...(dateRange.from ? { fromDate: dateRange.from } : {}),
      ...(dateRange.to ? { toDate: dateRange.to } : {}),
      ...(selectedTypes.includes('news') && selectedTypes.length === 1
        ? { kind: 'news' }
        : {}),
      ...(selectedTypes.filter(t => t !== 'news').length > 0
        ? { documentType: selectedTypes.filter(t => t !== 'news').join(',') }
        : {}),
    }),
    [dateRange, selectedTypes]
  );

  const query = useMemo(
    () => ({
      ...baseQuery,
      ...(selectedGeographies.length > 0
        ? { geography: selectedGeographies.join(',') }
        : {}),
      ...(selectedSources.length > 0
        ? { source: selectedSources.join(',') }
        : {}),
    }),
    [baseQuery, selectedGeographies, selectedSources]
  );

  const { updates, isLoading, isFetching } = useConsultantUpdatesService(
    orgId,
    query
  );

  const { updates: allUpdatesForFilters } = useConsultantUpdatesService(
    orgId,
    baseQuery
  );

  // Apply client-side filters (search + analyzed)
  const filteredUpdates = useMemo(() => {
    let result = updates;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        u =>
          (u.reference?.title ?? '').toLowerCase().includes(q) ||
          (u.title ?? '').toLowerCase().includes(q) ||
          (u.shortSummary ?? '').toLowerCase().includes(q) ||
          (u.source?.title ?? '').toLowerCase().includes(q) ||
          (u.consultantShortSummary ?? '').toLowerCase().includes(q)
      );
    }
    if (selectedAnalyzed.length === 1) {
      if (selectedAnalyzed.includes('yes')) result = result.filter(isAnalyzed);
      else result = result.filter(u => !isAnalyzed(u));
    }
    return result;
  }, [updates, selectedAnalyzed, searchQuery]);

  const geographyCodes = useMemo(() => {
    const geos = new Set<string>();
    for (const u of allUpdatesForFilters) {
      if (u.source?.geography) geos.add(u.source.geography);
      for (const g of u.source?.institutionGeographies ?? []) {
        geos.add(g);
      }
    }
    return [...geos].sort(compareGeographyCodesForFilter);
  }, [allUpdatesForFilters]);

  const sourceOptions: FilterOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const u of allUpdatesForFilters) {
      if (u.source) seen.set(u.source.id, u.source.title);
    }
    return [...seen.entries()]
      .map(([id, title]) => ({ value: id, label: title }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allUpdatesForFilters]);

  const columns: ColumnDef<ConsultantUpdateItem>[] = useMemo(
    () => [
      {
        id: 'date',
        header: 'Date',
        size: 100,
        cell: ({ row }) => (
          <span className="text-xs text-neutral-500 whitespace-nowrap">
            {dayjs(row.original.publicationDate).format('DD/MM/YYYY')}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        size: 100,
        cell: ({ row }) => {
          const u = row.original;
          const displayType =
            u.kind === 'news' ? 'news' : (u.reference?.type ?? 'regulation');
          const typeConfig = TYPE_BADGE[displayType];
          if (!typeConfig) return null;
          const TypeIcon = typeConfig.icon;
          return (
            <Badge
              className={`h-5 gap-1 text-[10px] border-transparent ${typeConfig.bg} text-neutral-900 shadow-none`}
            >
              {TypeIcon && <TypeIcon className="size-3" />}
              {typeConfig.label}
            </Badge>
          );
        },
      },
      {
        id: 'title',
        header: 'Title',
        cell: ({ row }) => {
          const u = row.original;
          const title = u.reference?.title ?? u.title ?? u.shortSummary ?? '-';
          return <span className="line-clamp-1 text-sm">{title}</span>;
        },
      },
      {
        id: 'link',
        header: '',
        size: 50,
        cell: ({ row }) =>
          row.original.materialUrl ? (
            <div className="text-center" onClick={e => e.stopPropagation()}>
              <a
                href={row.original.materialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="size-3.5" />
              </a>
            </div>
          ) : null,
      },
      {
        id: 'source',
        header: 'Source',
        meta: {
          headerClassName: 'max-w-[200px]',
          cellClassName: 'max-w-[200px]',
        },
        cell: ({ row }) => (
          <span className="block truncate text-xs text-neutral-600">
            {row.original.source?.title ?? '-'}
          </span>
        ),
      },
      {
        id: 'region',
        header: 'Region',
        meta: {
          headerClassName: 'min-w-[100px]',
          cellClassName: 'min-w-[100px]',
        },
        cell: ({ row }) => {
          const source = row.original.source;
          if (!source) return null;
          return (
            <MultiGeographyBadge
              geography={source.geography ?? ''}
              institutionGeographies={source.institutionGeographies}
              institutionName={null}
            />
          );
        },
      },
      {
        id: 'analyzed',
        header: 'Analyzed',
        size: 80,
        cell: ({ row }) =>
          isAnalyzed(row.original) ? (
            <div className="text-center">
              <Check className="size-4 text-green-600 mx-auto" />
            </div>
          ) : null,
      },
    ],
    []
  );

  const { pagination } = usePagination({
    initialSize: 50,
    manualPagination: false,
  });

  return (
    <FlexCol gap="6" w="full" p="6" className="max-w-[1600px]">
      <div>
        <h2 className="text-xl font-semibold">Regulatory Updates</h2>
        <p className="text-sm text-muted-foreground mt-1">
          View updates and write summaries and analyses for your clients
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <Input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search updates"
          className="pl-9"
        />
      </div>

      {/* Filters */}
      <FlexRow gap="2" alignItems="center" className="flex-wrap">
        <DateRangeSelect
          value={dateRange}
          onChange={setDateRange}
          label="Publication date"
          placeholder="Publication date"
          calendarOnly
        />
        <CountryFilter
          label="Region"
          countries={geographyCodes}
          selected={selectedGeographies}
          onChange={setSelectedGeographies}
        />
        <OptionFilter
          label="Source"
          options={sourceOptions}
          selected={selectedSources}
          onChange={setSelectedSources}
        />
        <OptionFilter
          label="Type"
          options={TYPE_OPTIONS}
          selected={selectedTypes}
          onChange={setSelectedTypes}
        />
        <OptionFilter
          label={`Analyzed by ${orgName}`}
          options={ANALYZED_OPTIONS}
          selected={selectedAnalyzed}
          onChange={setSelectedAnalyzed}
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filteredUpdates.length} update(s)
        </span>
      </FlexRow>

      <div
        className={
          isFetching && !isLoading
            ? 'opacity-60 transition-opacity duration-200'
            : ''
        }
      >
        <DataTable
          columns={columns}
          data={filteredUpdates}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={filteredUpdates.length}
          onRowClick={u => setEditItem(u)}
          options={{
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
          }}
        />
      </div>

      {editItem && (
        <EditSummaryModal
          open={!!editItem}
          onOpenChange={open => !open && setEditItem(null)}
          update={editItem}
        />
      )}
    </FlexCol>
  );
};
